import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { createSelector } from '@reduxjs/toolkit';

import { otherColors } from '@/styles/colors';
import { flex, sizing, media } from '@/styles/theme';
import { SIDEBAR_DELAY } from '@/config/variables';
import { ChevronUp } from '@/assets/icons';
import { useAppSelector } from '@/hooks';
import { selectIsMobileScreenSize } from '@/store/selectors/utilsSelectors';

interface ICard {
  icon?: string;
  name?: string;
  rightText?: string;
  children?: React.ReactNode;
  maxBodyHeight?: number;
  bodyHeight?: number;
}

const cardSelector = createSelector(selectIsMobileScreenSize, isMobileScreenSize => ({ isMobileScreenSize }));

const Card = ({ icon, name, rightText, children, maxBodyHeight, bodyHeight }: ICard): React.ReactElement => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const { isMobileScreenSize } = useAppSelector(cardSelector);

  useEffect(() => {
    setIsCollapsed(isMobileScreenSize);
  }, [isMobileScreenSize]);

  return (
    <SidebarItemContainer>
      <SidebarItemTitle
        onClick={() => {
          if (isMobileScreenSize) setIsCollapsed(!isCollapsed);
        }}
        className={isMobileScreenSize ? 'is-mobile-screen' : ''}
      >
        {icon && <SidebarIcon icon={icon} />}
        <MainTitleText>{name}</MainTitleText>
        {rightText && <RightTitleText>{rightText}</RightTitleText>}
        <CollapseIcon className={isCollapsed ? 'collapsed' : ''} />
      </SidebarItemTitle>
      {!isCollapsed && (
        <BodyContainer maxBodyHeight={maxBodyHeight} bodyHeight={bodyHeight}>
          {children}
        </BodyContainer>
      )}
    </SidebarItemContainer>
  );
};

export { Card };

const SidebarItemContainer = styled.div`
  margin-bottom: 12px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.075);
`;

const SidebarItemTitle = styled.div.attrs({
  className: `${flex.rowCenter}`,
})`
  color: ${otherColors.sidebar.text};
  background-color: ${otherColors.sidebar.background};
  font-size: ${sizing.fontSize.base};
  font-weight: bold;
  line-height: 24px;
  text-transform: uppercase;

  padding-left: 14px;
  padding-right: 14px;
  height: 56px;

  border-top-left-radius: 4px;
  border-top-right-radius: 4px;

  transition: opacity ${SIDEBAR_DELAY}ms ease-in-out;

  div {
    &:first-child + div {
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;

      & + div {
        font-weight: normal;
      }
    }
  }

  &.is-mobile-screen {
    cursor: pointer;
  }
`;

const SidebarIcon = styled.div<{ icon: string }>`
  width: 24px;
  height: 24px;
  background-color: #0087dd;
  mask-image: url(${({ icon }) => icon});
  mask-repeat: no-repeat;
  mask-position: left;
  mask-size: contain;
`;

const CollapseIcon = styled.div`
  position: absolute;
  right: 50px;
  width: 12px;
  height: 7px;
  background-color: #0087dd;
  mask-image: url(${ChevronUp});
  mask-repeat: no-repeat;
  mask-position: right;
  mask-size: contain;

  transition: transform 0.3s ease-in-out;

  &.collapsed {
    transform: rotate(180deg);
  }

  ${media.tablet} {
    display: none;
  }
`;

const MainTitleText = styled.div`
  margin-left: 12px;
  margin-right: 20px;
`;

const RightTitleText = styled.div`
  margin-left: auto;
`;

const BodyContainer = styled.div<{ maxBodyHeight?: number; bodyHeight?: number }>`
  background-color: #fff;
  overflow-y: scroll;

  ${({ bodyHeight }) => bodyHeight && `height: ${bodyHeight}px;`}
  ${({ maxBodyHeight }) => maxBodyHeight && `max-height: ${maxBodyHeight}px;`}
`;
